import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserStore } from '../stores/userStore';

interface EmployerAuthRouteProps {
  children: React.ReactNode;
}

/**
 * EmployerAuthRoute — guards employer-only pages.
 * - Unauthenticated users → /employer-login
 * - Authenticated workers (non-employers) → /dashboard
 * - Authenticated employers → renders children
 *
 * Use this instead of wrapping employer routes with AuthenticatedRoute + EmployerRoute,
 * so that the redirect goes to /employer-login (not the worker /sign-in).
 */
const EmployerAuthRoute: React.FC<EmployerAuthRouteProps> = ({ children }) => {
  const { isAuthenticated, hasToken, isInitialized, isLoading, init, user } = useUserStore();
  const [hydrated, setHydrated] = useState(
    useUserStore.persist.hasHydrated()
  );

  useEffect(() => {
    if (hydrated) return;
    const unsub = useUserStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });
    return unsub;
  }, [hydrated]);

  useEffect(() => {
    if (hydrated) {
      init();
    }
  }, [hydrated, init]);

  if (!hydrated || !isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/employer-login" replace />;
  }

  if (!user?.is_employer) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default EmployerAuthRoute;
